import { useState, useRef } from 'react';
import { D, st, toArr } from '../constants';

export default function FieldNotesPanel({notes, onSaveNote, onDeleteNote}) {
  const [noteText, setNoteText] = useState("");
  const [noteAuthor, setNoteAuthor] = useState("");
  const [photos, setPhotos] = useState([]);
  const [listeningActive, setListeningActive] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const photoInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const voiceSupported = !!SpeechRecognition;

  const addPhoto = (files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = ev => {
        setPhotos(p => [...p, {id:Date.now()+Math.random(), dataUrl:ev.target.result, name:file.name}]);
      };
      reader.readAsDataURL(file);
    });
  };
  const removePhoto = (photoId) => setPhotos(p => p.filter(x=>x.id!==photoId));

  const startVoice = () => {
    if (!SpeechRecognition) return;
    if (listeningActive) {
      if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
      setListeningActive(false);
      return;
    }
    const r = new SpeechRecognition();
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    let finalSoFar = "";
    r.onstart = () => setListeningActive(true);
    r.onresult = (ev) => {
      let interim = "", finalNew = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) finalNew += ev.results[i][0].transcript;
        else interim += ev.results[i][0].transcript;
      }
      if (finalNew) finalSoFar += finalNew;
      setNoteText(prev => (prev ? prev.trimEnd()+" " : "") + finalSoFar + (interim ? " "+interim : ""));
    };
    r.onerror = (ev) => { if (ev.error !== "aborted") setListeningActive(false); };
    r.onend = () => { setListeningActive(false); recognitionRef.current = null; };
    recognitionRef.current = r;
    r.start();
  };

  const submit = () => {
    if (!noteText.trim() && !photos.length) return;
    const now = new Date();
    const ts = now.toLocaleDateString("en-US",{month:"short",day:"numeric"}) + ", " + now.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
    onSaveNote({id:Date.now(), text:noteText.trim(), author:(noteAuthor.trim()||"Crew"), ts, photos:[...photos]});
    setNoteText(""); setPhotos([]);
  };

  return (
    <div style={{borderTop:`1px solid ${D.border}`, paddingTop:14, marginTop:4}}>
      <div style={{fontSize:13, fontWeight:500, color:D.muted, marginBottom:10}}>📋 Field Notes</div>

      {toArr(notes).length === 0 ? (
        <div style={{fontSize:12, color:D.muted, marginBottom:10}}>No notes yet — add the first one below.</div>
      ) : (
        <div style={{marginBottom:12, display:"flex", flexDirection:"column", gap:8}}>
          {toArr(notes).map(note => (
            <div key={note.id} style={{background:D.surface, border:`1px solid ${D.border}`, borderRadius:8, padding:"10px 12px"}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:5, gap:8}}>
                <div style={{display:"flex", alignItems:"center", gap:7}}>
                  <span style={{background:D.accent+"22", color:D.accent, fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:5, border:`1px solid ${D.accent}33`}}>{note.author}</span>
                  <span style={{fontSize:11, color:D.muted}}>{note.ts}</span>
                </div>
                <button onClick={()=>onDeleteNote(note.id)} style={{background:"none", border:"none", color:D.muted, cursor:"pointer", fontSize:16, padding:"0 2px", lineHeight:1}}>×</button>
              </div>
              {note.text ? <div style={{fontSize:13, color:D.text, lineHeight:1.5, whiteSpace:"pre-wrap", marginBottom:toArr(note.photos).length?8:0}}>{note.text}</div> : null}
              {toArr(note.photos).length > 0 && (
                <div style={{display:"flex", flexWrap:"wrap", gap:6, marginTop:4}}>
                  {toArr(note.photos).map(photo => (
                    <div key={photo.id} onClick={()=>setLightboxPhoto(photo.dataUrl)} style={{cursor:"pointer", borderRadius:6, overflow:"hidden", border:`1px solid ${D.border}`, width:72, height:72, flexShrink:0}}>
                      <img src={photo.dataUrl} alt={photo.name} style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{background:D.surface, border:`1px solid ${D.border}`, borderRadius:8, padding:"10px 12px"}}>
        <input
          style={{...st.input, marginBottom:8, fontSize:12}}
          placeholder="Your name (e.g. Adam)"
          value={noteAuthor}
          onChange={e=>setNoteAuthor(e.target.value)}
        />
        {photos.length > 0 && (
          <div style={{display:"flex", flexWrap:"wrap", gap:6, marginBottom:8}}>
            {photos.map(photo => (
              <div key={photo.id} style={{position:"relative", width:64, height:64, borderRadius:6, overflow:"hidden", border:`1px solid ${D.border}`, flexShrink:0}}>
                <img src={photo.dataUrl} alt={photo.name} style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}}/>
                <button onClick={()=>removePhoto(photo.id)} style={{position:"absolute", top:2, right:2, background:"rgba(0,0,0,0.65)", border:"none", color:"#fff", borderRadius:"50%", width:18, height:18, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, padding:0}}>×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{display:"flex", gap:8, alignItems:"flex-start"}}>
          <div style={{flex:1, position:"relative"}}>
            <textarea
              style={{...st.input, width:"100%", height:72, resize:"none", fontSize:13, lineHeight:1.4, border:listeningActive?`1.5px solid ${D.danger}`:`1px solid ${D.border}`}}
              placeholder={listeningActive ? "🎙 Listening… speak your note" : "What did you do, find, or need? (materials used, issues found, next steps…)"}
              value={noteText}
              onChange={e=>setNoteText(e.target.value)}
              onKeyDown={e=>{ if (e.key==="Enter" && (e.metaKey||e.ctrlKey)) submit(); }}
            />
            {listeningActive && (
              <div style={{position:"absolute", top:6, right:8, display:"flex", alignItems:"center", gap:4}}>
                <span style={{width:7, height:7, borderRadius:"50%", background:D.danger, display:"inline-block"}}/>
                <span style={{fontSize:10, color:D.danger, fontWeight:600}}>REC</span>
              </div>
            )}
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:6}}>
            <button title="Add photo"
              style={{...st.btn("default"), marginLeft:0, padding:"0", width:44, height:44, fontSize:20, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}
              onClick={()=>{
                if (!photoInputRef.current) {
                  const inp = document.createElement("input");
                  inp.type = "file"; inp.accept = "image/*"; inp.multiple = true; inp.capture = "environment";
                  inp.onchange = ev => addPhoto(ev.target.files);
                  photoInputRef.current = inp;
                }
                photoInputRef.current.click();
              }}
            >📷</button>
            {voiceSupported && (
              <button
                title={listeningActive ? "Stop recording" : "Tap to dictate"}
                style={{...st.btn(listeningActive?"danger":"default"), marginLeft:0, padding:"0", width:44, height:44, fontSize:20, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, border:listeningActive?`2px solid ${D.danger}`:`1px solid ${D.border}`}}
                onClick={startVoice}
              >{listeningActive ? "⏹" : "🎙"}</button>
            )}
            <button title="Save note"
              style={{...st.btn("primary"), marginLeft:0, padding:"0", width:44, height:44, fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}
              onClick={submit}
            >✓</button>
          </div>
        </div>
        <div style={{fontSize:10, color:D.muted, marginTop:5}}>
          {voiceSupported ? "📷 photo · 🎙 dictate · ✓ save · Ctrl+Enter shortcut" : "📷 add photo · ✓ save · Ctrl+Enter shortcut"}
        </div>
      </div>

      {lightboxPhoto && (
        <div onClick={()=>setLightboxPhoto(null)} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", cursor:"zoom-out"}}>
          <img src={lightboxPhoto} alt="Field note photo" style={{maxWidth:"95vw", maxHeight:"92vh", borderRadius:8, boxShadow:"0 4px 40px rgba(0,0,0,0.6)", objectFit:"contain"}}/>
          <button onClick={()=>setLightboxPhoto(null)} style={{position:"absolute", top:16, right:20, background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", fontSize:26, cursor:"pointer", borderRadius:"50%", width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center"}}>×</button>
        </div>
      )}
    </div>
  );
}
